import { Component, OnInit, Input, EventEmitter, Output, SimpleChanges, inject } from '@angular/core';
import { FormElementComponent } from '../form-element/form-element.component';
import { Tag } from 'src/app/shared/models/tags';
import { FormControl } from '@angular/forms';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { ExperienceI18nService } from 'src/app/core/experience/experience-i18n.service';


@Component({
  selector: 'app-multi-select-searching-input',
  templateUrl: './multi-select-searching-input.component.html',
  styleUrl: './multi-select-searching-input.component.scss'
})
export class MultiSelectSearchingInputComponent extends FormElementComponent implements OnInit {

    private experienceI18n: ExperienceI18nService = inject(ExperienceI18nService);
    @Input() defaultValue: number[] = [];
    @Input() spinner = false;
    // Input provided that indicates if the values of the mat-options will be computed as strings or numbers
    @Input() forceNumber!: string;
    // Functions provided by the parent that tells the component how to calculate the id and name of the options
    @Input() calculateId: (element:any) => any = (e) => e.id;
    @Input() calculateName: (element:any) => any = (e) => e.name;
    @Input() multiple!: boolean; // Undefined by default
    @Input() nameSearching = 'nameSearching';
    @Input() placeholderSearching = this.experienceI18n.translate('shared.multi-select-searching-input.placeholders.searching', undefined, 'Buscar...');
    @Input() placeholderNoValue = this.experienceI18n.translate('shared.multi-select-searching-input.placeholders.no-value', undefined, 'Sin resultados');
    @Input() searchingSpinner = false;
    // @Input() noOptionText = 'No hay opciones disponibles';

    // Emit an event every time the select changes
    @Output() public selected: EventEmitter<any> = new EventEmitter();
    @Input() valuesSelected!: Array<{ id: string, name: string } | Tag>;

    private _elements: any[] = [];
    private elements$ = new BehaviorSubject<any[]>([]);
    filteredValues!: Observable<any[]>;
    @Input() set elements(val: any[]) {
        this._elements = Array.isArray(val) ? val : [];
        this.elements$.next(this._elements);
      }
      get elements() { return this._elements; }


    constructor() {
        super();
    }

    // Static function that returns the appropriate function according to the "calculate" parameter
    static proxyCalculate(calculate: any): (element: any) => string {
        if (typeof calculate === 'string') {
            return element => {
                return element ? element[String(calculate)] : null;
            };
        } else {
            return calculate;
        }
    }

    // Proxy that calls the proxyCalculate function with a function or string that tells this component how to calculate the Id
    proxyCalculateId(): (element: any) => string {
        return MultiSelectSearchingInputComponent.proxyCalculate(this.calculateId);
    }

    // Proxy that calls the proxyCalculate function with a function or string that tells this component how to calculate the Name
    proxyCalculateName(): (element: any) => string {
        return MultiSelectSearchingInputComponent.proxyCalculate(this.calculateName);
    }

    override ngOnInit() {
        if (this.forceNumber) {
            this.form.get(this.name)?.valueChanges.subscribe(value => {
                if (typeof value === 'string') {
                    this.form.get(this.name)?.setValue(Number(value));
                }
            });
        }

        // Si valuesSelected está definido, establece el valor en el FormControl
        if (this.valuesSelected) {
            const initialValues = this.valuesSelected.map(value => value.id);
            this.form.get(this.name)?.setValue(initialValues);
            this.form.get(this.name)?.updateValueAndValidity();
        }

        if (!this.form.contains(this.nameSearching)) {
            this.form.addControl(this.nameSearching, new FormControl(''));
          }

          const search$ = this.form.get(this.nameSearching)!.valueChanges.pipe(
            startWith(''),
            debounceTime(150),
            distinctUntilChanged(),
            map((v: string) => (v ?? '').toLowerCase().trim())
          );

          // Filtrado por "name" (ajustá el campo si usás otro)
          this.filteredValues = combineLatest([this.elements$, search$]).pipe(
            map(([elems, term]) => {
              if (!term) return elems;
              return elems.filter(e => (e?.name ?? '').toLowerCase().includes(term));
            })
          );
    }

    calculateSelected(id: string): string {
        let elementSelected = null;
        if (this.elements) {
            elementSelected = this.elements.find(
                element =>
                    this.proxyCalculateId()(element) ===
                    (this.forceNumber ? Number(id) : id)
            );
        }
        return elementSelected;
    }

    change(event: any) {
        console.log('Valor seleccionado:', event.value);

        // Actualizamos valuesSelected con los elementos que coinciden con los valores seleccionados
        this.valuesSelected = this.elements.filter(element => event.value.includes(element.id));
        console.log('Valores seleccionados después del cambio:', this.valuesSelected);

        // Actualizamos el FormControl
        this.form.get(this.name)?.setValue(event.value);
        this.form.get(this.name)?.updateValueAndValidity();
        console.log('Valor del FormControl:', this.form.get(this.name)?.value);

        // Emitimos los valores seleccionados al componente padre
        this.selected.emit(this.valuesSelected);
    }


    removeElement(element: any) {
        this.valuesSelected = this.valuesSelected.filter(value => value.id != element.id);

        // Actualizamos el FormControl para que refleje los cambios
        const updatedValues = this.valuesSelected.map(value => value.id);
        this.form.get(this.name)?.setValue(updatedValues);
        this.form.get(this.name)?.updateValueAndValidity();

        // Emitimos los valores seleccionados actualizados
        this.selected.emit(this.valuesSelected);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['valuesSelected']) {
            const updatedValues = this.valuesSelected?.map(value => value.id);
            this.form.get(this.name)?.setValue(updatedValues);
            this.form.get(this.name)?.updateValueAndValidity();
        }
    }

    getError() {
        const control = this.form.get(this.name);
        if (control && control.errors) {
            return this.objectValues(control.errors)[0];
        }
        return null;
    }
}

